import { useLocation, useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';

export default function Destination() {
  const { id } = useParams();
  const { state } = useLocation();

  const items = useMemo(() => ([
    { id: 1, t: 'Famous Area', name: 'Eiffel District', place: 'Paris, France', img: 'area1', desc: 'Iconic landmarks, quaint cafes, and romantic river walks.' },
    { id: 2, t: 'Famous Area', name: 'Times Square', place: 'New York, USA', img: 'area2', desc: 'Neon-lit nights, Broadway shows, and endless city energy.' },
    { id: 3, t: 'Mountains', name: 'Swiss Alps', place: 'Bern, Switzerland', img: 'mt1', desc: 'Snow-capped peaks, scenic trains, and alpine adventures.' },
    { id: 4, t: 'Mountains', name: 'Himalayas', place: 'Nepal', img: 'mt2', desc: 'World’s highest mountains, spiritual treks, and serene vistas.' },
    { id: 5, t: 'Cities', name: 'Singapore City', place: 'Singapore', img: 'city1', desc: 'Futuristic skyline, diverse food, and lush urban gardens.' },
    { id: 6, t: 'Cities', name: 'Tokyo Center', place: 'Tokyo, Japan', img: 'city2', desc: 'Tradition meets tech with temples, anime, and gourmet sushi.' },
    { id: 7, t: 'Nature & Wildlife', name: 'Safari Plains', place: 'Kenya', img: 'nat1', desc: 'Big 5 safaris, golden savannas, and dramatic sunsets.' },
    { id: 8, t: 'Nature & Wildlife', name: 'Amazon Edge', place: 'Brazil', img: 'nat2', desc: 'Rainforest cruises, diverse wildlife, and riverside lodges.' },
    { id: 9, t: 'Famous Area', name: 'Colosseum', place: 'Rome, Italy', img: 'area3', desc: 'Ancient arenas, cobbled streets, and timeless cuisine.' },
    { id:10, t: 'Mountains', name: 'Rocky Ridge', place: 'Alberta, Canada', img: 'mt3', desc: 'Turquoise lakes, pine forests, and rugged peaks.' },
    { id:11, t: 'Cities', name: 'Old Town', place: 'Prague, Czechia', img: 'city3', desc: 'Storybook streets, castles, and river bridges.' },
    { id:12, t: 'Nature & Wildlife', name: 'Great Barrier', place: 'Queensland, AUS', img: 'nat3', desc: 'Coral reefs, snorkeling, and island getaways.' },
  ]), []);

  const numericId = Number(id);
  const fromState = state && typeof state === 'object' ? state : null;
  const data = fromState ?? items.find(x => x.id === numericId);

  if (!data) {
    return (
      <section className="container py-16">
        <h1 className="text-2xl font-semibold">Destination not found</h1>
        <p className="text-gray-600 mt-2">The destination you are looking for does not exist.</p>
        <Link to="/" className="btn-outline mt-6 inline-flex">Back to Home</Link>
      </section>
    );
  }

  return (
    <section>
      {/* Hero */}
      <div className="relative h-[44vh] md:h-[54vh]">
        <img alt={data.name} src={`https://picsum.photos/seed/${data.img}/1600/900`} className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"/>
        <div className="absolute inset-0 flex items-end">
          <div className="container pb-8">
            <nav className="text-xs text-white/80">
              <Link to="/" className="hover:underline">Home</Link>
              <span className="mx-1">/</span>
              <Link to="/" className="hover:underline">Discover</Link>
              <span className="mx-1">/</span>
              <span className="text-white/90">{data.name}</span>
            </nav>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs text-gray-700">{data.t}</div>
            <h1 className="text-white text-4xl md:text-5xl font-extrabold mt-3">{data.name}</h1>
            <div className="mt-1 text-white/90 flex items-center gap-3">
              <span>{data.place}</span>
              <span className="inline-flex items-center gap-1 text-amber-400">
                {Array.from({length:5}).map((_,i)=> (
                  <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i<4?'currentColor':'none'} stroke="currentColor"><path strokeWidth="1.4" d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z"/></svg>
                ))}
                <span className="text-xs text-white/80">4.0</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container py-10 grid lg:grid-cols-[2fr,1fr] gap-8">
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Overview</h2>
              <span className="text-xs text-gray-500">ID #{data.id}</span>
            </div>
             <div className="text-md text-gray-500 mt-1">
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s,
              </div>
            <p className="text-gray-700 mt-2 leading-relaxed">{data.desc}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold">Itinerary snapshot</h3>
              <ul className="mt-3 text-sm text-gray-700 space-y-2">
                <li>Day 1: Arrival, hotel check‑in, local walk</li>
                <li>Day 2: Guided tour and culinary experience</li>
                <li>Day 3: Free exploration and sunset point</li>
                <li>Day 4: Shopping and departure</li>
              </ul>
            </div>
            <div className="card p-6">
              <h3 className="text-lg font-semibold">Amenities</h3>
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                <div>• Airport pickup</div>
                <div>• Breakfast included</div>
                <div>• Local guide</div>
                <div>• 24/7 support</div>
              </div>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="card p-6">
            <div className="text-2xl font-bold">$999 <span className="text-gray-400 text-sm font-normal line-through ml-2">$1299</span></div>
            <div className="text-xs text-gray-500">Per Traveler • Taxes included</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <input type="date" className="rounded-xl border border-gray-200 px-3 py-2 text-sm"/>
              <input type="date" className="rounded-xl border border-gray-200 px-3 py-2 text-sm"/>
            </div>
            <button className="btn-primary w-full mt-4">Book Now</button>
            <button className="btn-outline w-full mt-2">Contact Agent</button>
          </div>
          <div className="card p-6">
            <h4 className="font-semibold">Good to know</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>Best season: Apr – Oct</li>
              <li>Avg. stay: 4–7 days</li>
              <li>Family friendly</li>
              <li>Free cancellation within 48 hours</li>
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}
